import React, { useState, useEffect } from "react";
import apiClient from "../../../api/apiClient";
import Loading from "../../../components/Loading/Loading";
import SequenceBuilder from "../../AdminPanel/tabs/CRM/campaign/components/SequenceBuilder";
import SafetyRules from "../../AdminPanel/tabs/CRM/campaign/components/SafetyRules";
import SendConfig from "../../AdminPanel/tabs/CRM/campaign/components/SendConfig";

const LEAD_TYPE_LABEL = { retailer_brand: "Retailer / Brand", grower: "Grower", consumer: "Consumer" };

const STATUSES   = ["new", "active", "won", "lost", "converted"];
const PRIORITIES = ["cold", "warm", "hot"];
const LEAD_TYPES = ["retailer_brand", "grower", "consumer"];

const defaultState = () => ({
    name: "",
    subject: "",
    steps: [],
    audienceFilters: { statuses: [], priorities: [], leadTypes: [] },
    sendConfig: { sendImmediately: true, scheduledAt: null, timeWindowOnly: false },
    safetyRules: { stopOnReply: true, excludeLost: true, skipContactedDays: null },
});

// Multi-select pill toggle helper
const togglePill = (arr, val) =>
    arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

const InboundCampaignBuilder = ({ campaignId, segmentId, segment, accentColor, onSave, onCancel }) => {
    const isGeneral = !segmentId;
    const [form, setForm]                         = useState(defaultState());
    const [loading, setLoading]                   = useState(!!campaignId);
    const [saving, setSaving]                     = useState(false);
    const [error, setError]                       = useState("");
    const [isReadOnly, setIsReadOnly]             = useState(false);
    const [campaignStatus, setCampaignStatus]     = useState("draft");

    useEffect(() => {
        if (!campaignId) return;
        setLoading(true);
        apiClient.get(`/admin/crm/campaigns/${campaignId}`)
            .then(res => {
                const { campaign, steps } = res.data;
                setCampaignStatus(campaign.status);
                setIsReadOnly(campaign.status === "active");
                const af = campaign.audienceFilters || {};
                setForm({
                    name: campaign.name,
                    subject: campaign.subject || "",
                    steps: steps.map(s => ({
                        type: s.type,
                        body: s.body || "",
                        delayDays: s.delayDays || 1,
                        subject: s.subject || "",
                    })),
                    audienceFilters: {
                        statuses:   Array.isArray(af.statuses)   ? af.statuses   : [],
                        priorities: Array.isArray(af.priorities) ? af.priorities : [],
                        leadTypes:  Array.isArray(af.leadTypes)  ? af.leadTypes  : [],
                    },
                    sendConfig: {
                        sendImmediately: !!campaign.sendImmediately,
                        scheduledAt: campaign.scheduledAt || null,
                        timeWindowOnly: !!campaign.timeWindowOnly,
                    },
                    safetyRules: {
                        stopOnReply: !!campaign.stopOnReply,
                        excludeLost: !!campaign.excludeLost,
                        skipContactedDays: campaign.skipContactedDays || null,
                    },
                });
            })
            .catch(() => setError("Failed to load campaign"))
            .finally(() => setLoading(false));
    }, [campaignId]);

    const validate = () => {
        if (!form.name.trim())    return "Campaign name is required";
        if (!form.subject.trim()) return "Email subject is required";
        if (form.steps.length === 0) return "Add at least one step";
        if (form.steps[0]?.type === "thread") return "First step cannot be a thread break";
        let stepNum = 0;
        for (let i = 0; i < form.steps.length; i++) {
            const s = form.steps[i];
            if (s.type === "thread") {
                if (!s.subject?.trim()) return "Thread break: subject is required";
            } else {
                stepNum++;
                if (s.type === "email" && !s.body?.trim()) return `Step ${stepNum}: body is required`;
                if (s.type === "delay" && !(s.delayDays >= 1)) return `Step ${stepNum}: delay must be ≥ 1 day`;
            }
        }
        return null;
    };

    const buildPayload = () => ({
        name: form.name.trim(),
        subject: form.subject.trim(),
        steps: form.steps,
        audienceFilters: isGeneral ? form.audienceFilters : {},
        sendImmediately: form.sendConfig.sendImmediately,
        scheduledAt: form.sendConfig.scheduledAt || null,
        timeWindowOnly: form.sendConfig.timeWindowOnly,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        stopOnReply: form.safetyRules.stopOnReply,
        excludeLost: form.safetyRules.excludeLost,
        skipContactedDays: form.safetyRules.skipContactedDays || null,
    });

    const handleSave = async (launch = false) => {
        const err = validate();
        if (err) { setError(err); return; }
        setError("");
        setSaving(true);
        try {
            let id = campaignId;
            if (!id) {
                const res = await apiClient.post("/admin/crm/inbound/campaigns", {
                    segmentId: segmentId || null,
                    ...buildPayload(),
                });
                id = res.data.id;
            } else {
                await apiClient.put(`/admin/crm/campaigns/${id}`, buildPayload());
            }
            if (launch) {
                await apiClient.post(`/admin/crm/campaigns/${id}/start`);
            }
            onSave(id);
        } catch (e) {
            setError(e.response?.data?.error || e.response?.data?.errors?.[0]?.msg || "Failed to save campaign");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="admin-loading-wrapper"><Loading /></div>;

    const filterChips = segment ? [
        segment.filters?.status   && <span key="s" className={`crm-status-badge crm-status-badge--${segment.filters.status}`}>{segment.filters.status}</span>,
        segment.filters?.priority && <span key="p" className={`crm-priority-badge crm-priority-badge--${segment.filters.priority}`}>{segment.filters.priority}</span>,
        segment.filters?.leadType && <span key="lt" className="iseg-chip">{LEAD_TYPE_LABEL[segment.filters.leadType] || segment.filters.leadType}</span>,
        segment.filters?.state    && <span key="st" className="iseg-chip">{segment.filters.state}</span>,
        segment.filters?.city     && <span key="c"  className="iseg-chip">{segment.filters.city}</span>,
    ].filter(Boolean) : [];

    const af = form.audienceFilters;

    return (
        <div className="crm-builder-layout">

            <button className="crm-builder-back" onClick={onCancel} type="button">
                ← Campaigns
            </button>

            <div className="crm-builder-title-row">
                <div className="crm-builder-title-row__heading">
                    {campaignId ? "Edit Campaign" : "New Campaign"}
                </div>
                <input
                    className="crm-builder-name-input"
                    placeholder="Give your campaign a name…"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    disabled={isReadOnly}
                />
            </div>

            {isReadOnly && (
                <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-4)", marginBottom: "var(--space-5)", fontSize: "var(--text-sm)", color: "#92400e" }}>
                    This campaign is currently active — pause it to edit the sequence.
                </div>
            )}

            <div className="crm-builder-grid">

                {/* ── Sidebar ── */}
                <div className="crm-builder-sidebar">

                    {/* Audience */}
                    <div className="crm-sidebar-section">
                        <div className="crm-sidebar-section__title">Audience</div>

                        {isGeneral ? (
                            /* General mode — basic inbound filters */
                            <div className="icam-audience-general">
                                <div className="icam-audience-general__hint">
                                    Targets all inbound leads matching the selected filters.
                                    Leave all empty to reach every active lead.
                                </div>

                                <div className="icam-filter-row">
                                    <div className="icam-filter-row__label">Status</div>
                                    <div className="icam-filter-pills">
                                        {STATUSES.map(s => (
                                            <button
                                                key={s}
                                                type="button"
                                                className={`icam-filter-pill${af.statuses.includes(s) ? " icam-filter-pill--active" : ""}`}
                                                onClick={() => !isReadOnly && setForm(f => ({ ...f, audienceFilters: { ...f.audienceFilters, statuses: togglePill(f.audienceFilters.statuses, s) } }))}
                                                disabled={isReadOnly}
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="icam-filter-row">
                                    <div className="icam-filter-row__label">Priority</div>
                                    <div className="icam-filter-pills">
                                        {PRIORITIES.map(p => (
                                            <button
                                                key={p}
                                                type="button"
                                                className={`icam-filter-pill${af.priorities.includes(p) ? " icam-filter-pill--active" : ""}`}
                                                onClick={() => !isReadOnly && setForm(f => ({ ...f, audienceFilters: { ...f.audienceFilters, priorities: togglePill(f.audienceFilters.priorities, p) } }))}
                                                disabled={isReadOnly}
                                            >
                                                {p}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="icam-filter-row">
                                    <div className="icam-filter-row__label">Lead Type</div>
                                    <div className="icam-filter-pills">
                                        {LEAD_TYPES.map(t => (
                                            <button
                                                key={t}
                                                type="button"
                                                className={`icam-filter-pill${af.leadTypes.includes(t) ? " icam-filter-pill--active" : ""}`}
                                                onClick={() => !isReadOnly && setForm(f => ({ ...f, audienceFilters: { ...f.audienceFilters, leadTypes: togglePill(f.audienceFilters.leadTypes, t) } }))}
                                                disabled={isReadOnly}
                                            >
                                                {LEAD_TYPE_LABEL[t]}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Segment mode — show segment info card */
                            <div className="icam-seg-info">
                                <svg className="icam-seg-info__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                    <circle cx="9" cy="7" r="4"/>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                </svg>
                                <div className="icam-seg-info__body">
                                    <div
                                        className="icam-seg-info__name"
                                        style={accentColor ? { color: accentColor } : {}}
                                    >
                                        {segment?.name || "Segment"}
                                    </div>
                                    <div className="icam-seg-info__meta">
                                        {segment?.leadCount != null && (
                                            <span>{segment.leadCount} lead{segment.leadCount !== 1 ? "s" : ""}</span>
                                        )}
                                        {filterChips.length > 0 && (
                                            <div className="iseg-row__chips" style={{ marginTop: "var(--space-1)" }}>
                                                {filterChips}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Safety */}
                    <div className="crm-sidebar-section">
                        <div className="crm-sidebar-section__title">Safety Rules</div>
                        <SafetyRules
                            rules={form.safetyRules}
                            onChange={v => setForm(f => ({ ...f, safetyRules: v }))}
                        />
                    </div>

                    {/* Sending */}
                    <div className="crm-sidebar-section">
                        <div className="crm-sidebar-section__title">Sending</div>
                        <SendConfig
                            config={form.sendConfig}
                            onChange={v => setForm(f => ({ ...f, sendConfig: v }))}
                        />
                    </div>

                </div>

                {/* ── Main: Sequence ── */}
                <div className="crm-builder-main">
                    <div className="crm-builder-section-heading">
                        <span className="crm-builder-section-heading__title">Email Sequence</span>
                        {form.steps.length > 0 && (
                            <span className="crm-builder-section-heading__meta">
                                {form.steps.length} step{form.steps.length !== 1 ? "s" : ""}
                            </span>
                        )}
                    </div>

                    {isReadOnly ? (
                        <div className="crm-sequence-groups">
                            {(() => {
                                const groups = [];
                                let current = { threadSubject: form.subject, steps: [] };
                                form.steps.forEach(s => {
                                    if (s.type === "thread") { groups.push(current); current = { threadSubject: s.subject, steps: [] }; }
                                    else current.steps.push(s);
                                });
                                groups.push(current);
                                let stepNum = 0;
                                return groups.map((g, gi) => (
                                    <div key={gi} className={`crm-thread-group crm-thread-group--readonly${gi === 0 ? " crm-thread-group--first" : ""}`}>
                                        <div className="crm-thread-group__header">
                                            <span className="crm-thread-group__label">{["1st","2nd","3rd"][gi] || `${gi+1}th`} Thread</span>
                                            <span className="crm-thread-group__subject-display">{g.threadSubject}</span>
                                        </div>
                                        <div className="crm-thread-group__body">
                                            <div className="crm-sequence-timeline">
                                                {g.steps.map((s, si) => {
                                                    stepNum++;
                                                    return (
                                                        <div key={si} className="crm-step-timeline-item">
                                                            <div className="crm-step-indicator">
                                                                <div className={`crm-step-indicator__circle crm-step-indicator__circle--${s.type}`}>{stepNum}</div>
                                                                {si < g.steps.length - 1 && <div className="crm-step-indicator__line" />}
                                                            </div>
                                                            <div className={`crm-step-card crm-step-card--${s.type}`} style={{ cursor: "default" }}>
                                                                <div className="crm-step-card__header">
                                                                    <span className="crm-step-card__title">
                                                                        {s.type === "email" ? "Email" : `Wait ${s.delayDays} day${s.delayDays !== 1 ? "s" : ""}`}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ));
                            })()}
                        </div>
                    ) : (
                        <SequenceBuilder
                            steps={form.steps}
                            onChange={v => setForm(f => ({ ...f, steps: v }))}
                            subject={form.subject}
                            onSubjectChange={v => setForm(f => ({ ...f, subject: v }))}
                        />
                    )}
                </div>
            </div>

            {/* ── Footer ── */}
            {!isReadOnly && (
                <div className="crm-builder-footer">
                    <button className="admin-btn" onClick={onCancel} disabled={saving} type="button">
                        Close
                    </button>
                    {campaignStatus === "paused" ? (
                        <>
                            <button
                                className="admin-btn"
                                onClick={async () => {
                                    setSaving(true);
                                    try {
                                        await apiClient.post(`/admin/crm/campaigns/${campaignId}/cancel`);
                                        setCampaignStatus("draft");
                                    } catch (e) {
                                        setError(e?.response?.data?.error || "Failed to cancel campaign");
                                    } finally {
                                        setSaving(false);
                                    }
                                }}
                                disabled={saving}
                                type="button"
                                style={{ color: "#dc2626", borderColor: "#dc2626" }}
                            >
                                Cancel Campaign
                            </button>
                            <button className="admin-btn admin-btn--primary" onClick={() => handleSave(false)} disabled={saving} type="button">
                                {saving ? <Loading /> : "Update Campaign"}
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="admin-btn admin-btn--primary" onClick={() => handleSave(false)} disabled={saving} type="button">
                                {saving ? <Loading /> : "Save as Draft"}
                            </button>
                            <button
                                className="admin-btn admin-btn--primary"
                                onClick={() => handleSave(true)}
                                disabled={saving}
                                type="button"
                                style={{ background: "#10b981", borderColor: "#10b981" }}
                            >
                                {saving ? <Loading /> : "Save & Launch ✓"}
                            </button>
                        </>
                    )}
                    {error && <span className="admin-form-error">{error}</span>}
                </div>
            )}
        </div>
    );
};

export default InboundCampaignBuilder;
